import { useEffect, useState, useCallback } from "react";
import { Megaphone, Loader2, RefreshCw, XCircle, Rocket, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";

const STATUS_OPTS = [["", "Tous statuts"], ["new", "Nouveau"], ["contacted", "Contacté"], ["interested", "Intéressé"], ["callback", "À rappeler"], ["no_answer", "Sans réponse"], ["rejected", "Pas intéressé"]];
const CAMP_STATUS = {
  scheduled: ["Programmée", "bg-[#FFF3E6] text-[#7A3A00]"], "in-progress": ["En cours", "bg-[#DCFCE7] text-[#166534]"],
  ended: ["Terminée", "bg-[#F3F4F6] text-[#374151]"], cancelled: ["Annulée", "bg-[#FEE2E2] text-[#991b1b]"],
};
const fmt = (d) => (d ? new Date(d).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Brussels" }) : "—");

function NewCampaign({ onCreated }) {
  const [form, setForm] = useState({ name: "", status: "new", commune: "", search: "", all: false });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const body = () => ({ name: form.name || "Campagne", limit: form.all ? 1000 : 100, filters: { status: form.status || null, commune: form.commune || null, search: form.search || null } });
  useEffect(() => { setPreview(null); }, [form.status, form.commune, form.search, form.all]);
  const doPreview = async () => {
    setBusy(true);
    try { setPreview((await api.post("/admin/vapi/campaigns/preview", body())).data); } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const create = async () => {
    if (form.name.trim().length < 2) return toast.error("Donnez un nom à la campagne.");
    if (!window.confirm(`Lancer « ${form.name} » : Sara Dupont appellera ${preview.count} restaurant(s) en ${preview.slots.length} créneau(x), entre ${fmt(preview.window.start)} et ${fmt(preview.window.end)} ?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/vapi/campaigns", body());
      toast.success(`${data.campaigns} campagne(s) créée(s) : ${data.count} restaurant(s)`);
      setForm({ ...form, name: "" }); setPreview(null); onCreated();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const inp = "h-9 px-2 rounded-lg border border-[#E2DFD8] text-xs bg-white";
  return (
    <div data-testid="campaign-new" className="rounded-xl bg-[#F8FAFF] p-3 space-y-2">
      <div className="text-xs font-bold text-[#1A1A1A]">Nouvelle campagne</div>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <input data-testid="campaign-name" className={inp} placeholder="Nom de la campagne" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <select data-testid="campaign-filter-status" className={inp} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
          {STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input data-testid="campaign-filter-commune" className={inp} placeholder="Commune (ex. Ixelles)" value={form.commune} onChange={(e) => setForm({ ...form, commune: e.target.value })} />
        <input data-testid="campaign-filter-search" className={inp} placeholder="Recherche (nom, cuisine…)" value={form.search} onChange={(e) => setForm({ ...form, search: e.target.value })} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs inline-flex items-center gap-1.5 text-[#3B423E]">
          <input data-testid="campaign-all" type="checkbox" checked={form.all} onChange={(e) => setForm({ ...form, all: e.target.checked })} /> Tous les prospects filtrés (sinon 100 max)
        </label>
        <button data-testid="campaign-preview-btn" onClick={doPreview} disabled={busy} className="h-8 px-3 rounded-lg bg-white border border-[#4F46E5] text-[#4F46E5] text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60">
          {busy && !preview ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Aperçu
        </button>
        {preview && (
          <>
            <span data-testid="campaign-preview" className="text-xs text-[#3B423E]"><b>{preview.count}</b> restaurant(s) · {preview.slots.length} créneau(x) d'1 h ({preview.slot_size} par créneau) · {fmt(preview.window.start)} → {fmt(preview.window.end)}</span>
            <button data-testid="campaign-create-btn" onClick={create} disabled={busy || !preview.count} className="h-8 px-3 rounded-lg bg-[#4F46E5] hover:bg-[#4338CA] text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60 ml-auto">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />} Lancer la campagne
            </button>
          </>
        )}
      </div>
      {preview?.sample?.length > 0 && <p className="text-[11px] text-[#75807A]">Ex. : {preview.sample.join(", ")}…</p>}
    </div>
  );
}

function CampaignRow({ c, onCancel, cancelling }) {
  const [label, cls] = CAMP_STATUS[c.status] || [c.status, "bg-[#F3F4F6] text-[#374151]"];
  const done = c.total ? Math.round((100 * c.ended) / c.total) : 0;
  const active = c.status === "scheduled" || c.status === "in-progress";
  const kpis = [[active ? "Restants" : "Non appelés", c.remaining], ["En cours", c.in_progress], ["Terminés", c.ended], ["Connectés", c.connected], ["Messagerie", c.voicemail], ["Décrochage", `${c.answer_rate} %`]];
  return (
    <div data-testid={`campaign-${c.id}`} className="rounded-xl border border-[#E0E7FF] bg-white p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-bold text-sm text-[#1A1A1A]">{c.name}</span>
        <span data-testid={`campaign-status-${c.id}`} className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${cls}`}>{label}</span>
        {!c.from_admin && <span className="text-[10px] text-[#75807A]">créée dans Vapi</span>}
        <span className="text-[11px] text-[#75807A]">{fmt(c.created_at)} · {c.total} restaurant(s)</span>
        {active && (
          <button data-testid={`campaign-cancel-${c.id}`} onClick={() => onCancel(c)} disabled={cancelling}
            className="ml-auto h-8 px-3 rounded-lg bg-[#FEE2E2] hover:bg-[#FECACA] text-[#991b1b] text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60">
            {cancelling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />} Annuler
          </button>
        )}
      </div>
      <div className="h-2 rounded-full bg-[#EEF2FF] overflow-hidden"><div className="h-full bg-[#4F46E5]" style={{ width: `${done}%` }} /></div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {kpis.map(([l, v]) => (
          <div key={l} data-testid={`campaign-kpi-${c.id}-${l}`} className="rounded-lg bg-[#F8FAFF] px-2 py-1.5">
            <div className="text-[10px] font-bold uppercase text-[#75807A]">{l}</div>
            <div className="text-base font-black text-[#1A1A1A]">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CampaignsPanel() {
  const [data, setData] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const load = useCallback(() => api.get("/admin/vapi/campaigns").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const hasActive = data?.items?.some((c) => c.status === "scheduled" || c.status === "in-progress");
  useEffect(() => {
    if (!hasActive) return undefined;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [hasActive, load]);
  const cancel = async (c) => {
    if (!window.confirm(`Annuler la campagne « ${c.name} » ? Les appels restants ne partiront pas.`)) return;
    const group = !!c.group_id && window.confirm("Annuler aussi tous les créneaux suivants de ce lancement ?");
    setCancelling(c.id);
    try { const { data } = await api.post(`/admin/vapi/campaigns/${c.id}/cancel${group ? "?group=true" : ""}`); toast.success(`${data.cancelled} campagne(s) annulée(s)`); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setCancelling(null); }
  };
  return (
    <div data-testid="campaigns-panel" className="rounded-2xl border border-[#E0E7FF] bg-white p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold text-[#1A1A1A] inline-flex items-center gap-1.5"><Megaphone className="w-4 h-4 text-[#4F46E5]" /> Campagnes Sara Dupont</div>
        <button data-testid="campaigns-refresh" onClick={load} className="h-8 px-3 rounded-lg bg-white border border-[#E2DFD8] text-xs font-semibold inline-flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> {hasActive ? "Temps réel (10 s)" : "Actualiser"}
        </button>
      </div>
      <NewCampaign onCreated={load} />
      {!data ? <div className="py-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#4F46E5]" /></div>
        : data.items.length === 0 ? <p data-testid="campaigns-empty" className="text-xs text-[#75807A]">Aucune campagne.</p>
        : <div className="space-y-2">{data.items.map((c) => <CampaignRow key={c.id} c={c} onCancel={cancel} cancelling={cancelling === c.id} />)}</div>}
      <p className="text-[11px] text-[#75807A]">Données Vapi. « Connectés » = conversations réelles (hors messagerie, sans réponse, occupé, échec) ; décrochage = connectés ÷ appels terminés. Les grandes listes sont découpées en créneaux d'1 h consécutifs (limite Vapi), toujours dans les horaires de Sara Dupont. Toute campagne Vapi qui tourne hors horaires est annulée automatiquement (vérification toutes les 15 min) avec un email d'alerte.</p>
    </div>
  );
}
